import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { LoadingSpinner } from '@/components/common/LoadingSpinner'
import { useQuery } from '@tanstack/react-query'
import { deviceService } from '@/services/device.service'
import { useAuthStore } from '@/stores/authStore'
import { formatUTCToLocal } from '@/utils/date'
import { HardDrive, Database, Settings, Radio, User } from 'lucide-react'
import type { Device } from '@/types/device.types'
import type { MeasurementListResponse } from '@/types/measurement.types'

interface DeviceDebugProps {
  device: Device
  measurementsData: MeasurementListResponse
}

export const DeviceDebug = ({ device }: DeviceDebugProps) => {
  const { user } = useAuthStore()

  const { data: debugData, isLoading, error } = useQuery({
    queryKey: ['device-debug', device.id],
    queryFn: () => deviceService.getDeviceDebug(device.id),
    enabled: !!user?.isSuperUser && !!device.id,
    staleTime: 30000,
  })

  if (!user?.isSuperUser) {
    return (
      <Card>
        <CardContent className="py-8">
          <p className="text-sm text-muted-foreground text-center">
            Debug information is only available for super users
          </p>
        </CardContent>
      </Card>
    )
  }

  if (isLoading) {
    return <LoadingSpinner />
  }

  if (error || !debugData) {
    return (
      <Card>
        <CardContent className="py-8">
          <p className="text-sm text-muted-foreground text-center">
            Failed to load debug information
          </p>
        </CardContent>
      </Card>
    )
  }

  const deviceInfo = debugData.device
  const accountInfo = debugData.account
  const parameters = debugData.parameters
  const latestMeasurement = debugData.latest_measurement
  const rawMessages = debugData.recent_raw_messages || []

  return (
    <div className="space-y-6">
      {/* Device Status */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <HardDrive className="h-5 w-5" />
            Device Status
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {Object.entries(deviceInfo || {}).map(([key, value]) => (
              <div key={key}>
                <p className="text-sm font-medium text-muted-foreground">{key}</p>
                <p className="text-sm font-mono break-all">
                  {value === null || value === undefined
                    ? '-'
                    : typeof value === 'boolean'
                      ? value ? 'true' : 'false'
                      : String(value)}
                </p>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Account Info */}
      {accountInfo && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <User className="h-5 w-5" />
              Account
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Account ID</p>
                <p className="text-sm font-mono">{accountInfo.id || '-'}</p>
              </div>
              <div>
                <p className="text-sm font-medium text-muted-foreground">Account Name</p>
                <p className="text-sm">{accountInfo.name || '-'}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Parameters */}
      {parameters && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Settings className="h-5 w-5" />
              Parameters
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {Object.entries(parameters).map(([key, value]) => (
                <div key={key}>
                  <p className="text-sm font-medium text-muted-foreground">{key}</p>
                  <p className="text-sm font-mono break-all">
                    {value === null || value === undefined
                      ? '-'
                      : typeof value === 'boolean'
                        ? value ? 'true' : 'false'
                        : String(value)}
                  </p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Latest Measurement */}
      {latestMeasurement && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Database className="h-5 w-5" />
              Latest Measurement
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {Object.entries(latestMeasurement).map(([key, value]) => (
                <div key={key}>
                  <p className="text-sm font-medium text-muted-foreground">{key}</p>
                  <p className="text-sm font-mono break-all">
                    {value === null || value === undefined
                      ? '-'
                      : typeof value === 'boolean'
                        ? value ? 'true' : 'false'
                        : String(value)}
                  </p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Recent Raw Messages */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Radio className="h-5 w-5" />
            Recent Raw Messages
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Message Type</TableHead>
                  <TableHead>Transmission Date</TableHead>
                  <TableHead>Created At</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rawMessages.length > 0 ? (
                  rawMessages.map((msg: any, idx: number) => (
                    <TableRow key={msg.message_id || idx}>
                      <TableCell>
                        <Badge variant="outline">{msg.message_type || '-'}</Badge>
                      </TableCell>
                      <TableCell>
                        {msg.transmission_date_utc
                          ? formatUTCToLocal(msg.transmission_date_utc)
                          : '-'}
                      </TableCell>
                      <TableCell>
                        {msg.created_at
                          ? formatUTCToLocal(msg.created_at)
                          : '-'}
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={3} className="text-center py-8 text-muted-foreground">
                      No raw messages available
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
